import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";
import {
  Lock,
  Eye,
  EyeOff,
  Trash2,
  ExternalLink,
  Download,
  LogOut,
  ShieldAlert,
  KeyRound,
  Copy,
  Plus,
  Check,
  Save,
  MessageCircle,
  Activity,
  RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminVerify,
  adminListEntries,
  adminToggleHide,
  adminDeleteEntry,
  setAdminPassword,
  clearAdminPassword,
  getAdminPassword,
  fileUrl,
  adminListCheckinTemplates,
  adminUpdateCheckinTemplate,
  adminListReservations,
  adminCreateReservation,
  adminDeleteReservation,
  adminGetAnalytics,
} from "../lib/api";
import { PROPERTIES } from "../lib/properties";

function Login({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setBusy(true);
    try {
      await adminVerify(pw);
      setAdminPassword(pw);
      toast.success("Signed in");
      onSuccess();
    } catch {
      toast.error("Wrong password");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="max-w-md mx-auto px-5 sm:px-6 pt-16 sm:pt-24 pb-24">
      <div className="gb-card p-8 sm:p-10">
        <div className="w-12 h-12 rounded-2xl bg-[#F4F4F4] flex items-center justify-center text-[#111111]">
          <Lock size={20} />
        </div>
        <h1 className="font-heading text-3xl font-extrabold text-[#111] tracking-tighter mt-5">
          Host sign in
        </h1>
        <p className="text-sm text-[#5b5b5b] mt-2">
          Enter the admin password to manage entries and QR codes.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input
            data-testid="admin-password-input"
            className="gb-input"
            type="password"
            placeholder="Admin password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={busy || !pw}
            className="gb-btn-primary w-full"
          >
            {busy ? "Checking..." : "Sign in"}
          </button>
        </form>
      </div>
    </section>
  );
}

function QRCodesPanel() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const downloadSvg = (id, name) => {
    const el = document.getElementById(`qr-${id}`);
    if (!el) return;
    const clone = el.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const source = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name.replace(/[\s/]+/g, "_")}-qr.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <div>
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[#111111] font-semibold">
            On-site QR codes
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#111] tracking-tighter mt-2">
            One QR per nest.
          </h2>
          <p className="text-sm text-[#5b5b5b] mt-2 max-w-xl">
            Print and place inside each unit. Scanning takes the guest straight
            to the gallery for that nest.
          </p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {PROPERTIES.map((p) => {
          const url = `${origin}/nest/${p.id}`;
          return (
            <div
              key={p.id}
              className="gb-card p-6 flex flex-col items-center text-center"
              data-testid={`qr-card-${p.id}`}
            >
              <div className="rounded-2xl border border-[#EBEBEB] p-4 bg-white">
                <QRCodeSVG
                  id={`qr-${p.id}`}
                  value={url}
                  size={168}
                  fgColor="#111111"
                  bgColor="#ffffff"
                  level="M"
                  includeMargin={false}
                />
              </div>
              <h3 className="font-heading font-bold text-[#111] tracking-tight mt-4">
                {p.fullName}
              </h3>
              <p className="text-xs text-[#717171] mt-0.5">{p.location}</p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#111111] mt-2 truncate max-w-full inline-flex items-center gap-1"
              >
                {url.replace(/^https?:\/\//, "")}
                <ExternalLink size={11} />
              </a>
              <button
                data-testid={`btn-download-qr-${p.id}`}
                onClick={() => downloadSvg(p.id, p.fullName)}
                className="gb-btn-ghost mt-4 inline-flex items-center gap-2 text-sm"
              >
                <Download size={14} /> Download SVG
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EntriesPanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | visible | hidden
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    adminListEntries()
      .then(setEntries)
      .catch(() => toast.error("Could not load entries"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "visible") return entries.filter((e) => !e.is_hidden);
    if (filter === "hidden") return entries.filter((e) => e.is_hidden);
    return entries;
  }, [entries, filter]);

  const nestNameById = useMemo(() => {
    const m = {};
    PROPERTIES.forEach((p) => (m[p.id] = p.fullName));
    return m;
  }, []);

  const handleHide = async (id) => {
    try {
      const res = await adminToggleHide(id);
      setEntries((cur) =>
        cur.map((e) => (e.id === id ? { ...e, is_hidden: res.is_hidden } : e)),
      );
      toast.success(res.is_hidden ? "Entry hidden" : "Entry made visible");
    } catch {
      toast.error("Action failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminDeleteEntry(id);
      setEntries((cur) => cur.filter((e) => e.id !== id));
      setConfirmDeleteId(null);
      toast.success("Entry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const counts = useMemo(
    () => ({
      all: entries.length,
      visible: entries.filter((e) => !e.is_hidden).length,
      hidden: entries.filter((e) => e.is_hidden).length,
    }),
    [entries],
  );

  return (
    <div className="mt-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[#111111] font-semibold">
            Entries
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#111] tracking-tighter mt-2">
            Manage gallery pages.
          </h2>
        </div>
        <div className="flex items-center gap-1 bg-white rounded-full p-1 border border-[#EBEBEB]">
          {[
            { k: "all", label: "All" },
            { k: "visible", label: "Visible" },
            { k: "hidden", label: "Hidden" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`filter-${t.k}`}
              onClick={() => setFilter(t.k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === t.k
                  ? "bg-[#222] text-white"
                  : "text-[#111] hover:bg-[#f2eeea]"
              }`}
            >
              {t.label} <span className="opacity-60">· {counts[t.k]}</span>
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="mt-8 text-[#717171]">Loading...</p>}

      {!loading && filtered.length === 0 && (
        <div className="mt-10 gb-card p-10 text-center text-[#717171]">
          No entries here.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((e) => (
            <motion.div
              key={e.id}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className={`gb-card overflow-hidden ${e.is_hidden ? "opacity-60" : ""}`}
              data-testid={`admin-entry-${e.id}`}
            >
              <div className="relative aspect-[4/5] bg-[#F0EDE7]">
                <img
                  src={fileUrl(e.image_path)}
                  alt={e.guest_name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                  {e.is_hidden && (
                    <span className="bg-[#222] text-white rounded-full px-2.5 py-1 text-[11px] font-medium flex items-center gap-1">
                      <EyeOff size={11} /> Hidden
                    </span>
                  )}
                  {e.nest_id && (
                    <span className="bg-white/95 rounded-full px-2.5 py-1 text-[11px] font-medium text-[#111] shadow-sm">
                      {nestNameById[e.nest_id] || e.nest_id}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5">
                <p className="font-heading font-bold text-[#111] tracking-tight">
                  {e.guest_name}
                </p>
                <p className="text-xs text-[#717171] mt-1">
                  {new Date(e.created_at).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                  {e.guest_email && (
                    <>
                      {" "}
                      · <span>{e.guest_email}</span>
                    </>
                  )}
                </p>
                {e.message && (
                  <p className="text-sm text-[#454545] mt-2 italic line-clamp-2">
                    &ldquo;{e.message}&rdquo;
                  </p>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to={`/gallery/${e.id}`}
                    data-testid={`admin-view-${e.id}`}
                    className="gb-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3"
                  >
                    <ExternalLink size={12} /> View
                  </Link>
                  <button
                    data-testid={`admin-toggle-hide-${e.id}`}
                    onClick={() => handleHide(e.id)}
                    className="gb-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3"
                  >
                    {e.is_hidden ? (
                      <>
                        <Eye size={12} /> Unhide
                      </>
                    ) : (
                      <>
                        <EyeOff size={12} /> Hide
                      </>
                    )}
                  </button>
                  <button
                    data-testid={`admin-delete-${e.id}`}
                    onClick={() => setConfirmDeleteId(e.id)}
                    className="text-xs inline-flex items-center gap-1 py-1.5 px-3 rounded-full text-white bg-[#111111] hover:bg-[#000000]"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {confirmDeleteId && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmDeleteId(null)}
        >
          <div
            className="gb-card p-6 sm:p-8 max-w-sm w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 mx-auto rounded-2xl bg-[#F4F4F4] flex items-center justify-center text-[#111111]">
              <ShieldAlert size={20} />
            </div>
            <h3 className="font-heading text-xl font-extrabold text-[#111] tracking-tighter mt-4">
              Delete this entry?
            </h3>
            <p className="text-sm text-[#5b5b5b] mt-2">
              This removes the guest&rsquo;s page permanently. The picture stays
              in storage but no longer appears anywhere.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                data-testid="admin-delete-cancel"
                onClick={() => setConfirmDeleteId(null)}
                className="gb-btn-ghost flex-1"
              >
                Cancel
              </button>
              <button
                data-testid="admin-delete-confirm"
                onClick={() => handleDelete(confirmDeleteId)}
                className="flex-1 rounded-full py-2.5 px-4 text-white bg-[#111111] hover:bg-[#000000] font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckinTemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNest, setOpenNest] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminListCheckinTemplates()
      .then(setTemplates)
      .catch(() => toast.error("Could not load check-in templates"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const nestMeta = useMemo(() => {
    const m = {};
    PROPERTIES.forEach((p) => (m[p.id] = p));
    return m;
  }, []);

  const activeTpl = openNest ? templates.find((t) => t.nest_id === openNest) : null;

  const setField = (nestId, field, value) => {
    setTemplates((cur) =>
      cur.map((t) => (t.nest_id === nestId ? { ...t, [field]: value } : t)),
    );
  };

  const setRulesText = (nestId, text) => {
    const arr = text
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    setField(nestId, "house_rules", arr);
  };

  const onSave = async (nestId) => {
    const tpl = templates.find((t) => t.nest_id === nestId);
    if (!tpl) return;
    setSaving(true);
    try {
      const { nest_id: _nid, updated_at: _ua, ...patch } = tpl;
      await adminUpdateCheckinTemplate(nestId, patch);
      toast.success(`Saved ${nestMeta[nestId]?.fullName || nestId}`);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-16">
      <div>
        <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[#111111] font-semibold">
          Self check-in templates
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#111] tracking-tighter mt-2">
          Details for every nest.
        </h2>
        <p className="text-sm text-[#5b5b5b] mt-2 max-w-xl">
          Fill in each nest&rsquo;s address, Wi-Fi, door code, and house rules
          once. New reservations pull from these templates.
        </p>
      </div>

      {loading && <p className="mt-6 text-[#717171]">Loading…</p>}

      {!loading && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => {
            const meta = nestMeta[t.nest_id];
            const done = Boolean(t.address && t.wifi_password && t.door_code);
            return (
              <button
                key={t.nest_id}
                type="button"
                data-testid={`open-template-${t.nest_id}`}
                onClick={() => setOpenNest(t.nest_id)}
                className="gb-card p-5 text-left hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={meta?.image}
                    alt={meta?.fullName}
                    className="w-14 h-14 rounded-xl object-cover"
                    loading="lazy"
                  />
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-[#111] tracking-tight">
                      {meta?.fullName || t.nest_id}
                    </p>
                    <p className="text-xs text-[#717171] truncate">
                      {meta?.location}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full ${
                      done
                        ? "bg-[#eaf7ea] text-[#1f6d2b]"
                        : "bg-[#faf4ea] text-[#8a5a15]"
                    }`}
                  >
                    {done ? <Check size={11} /> : <KeyRound size={11} />}
                    {done ? "Ready" : "Needs details"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Editor modal */}
      {activeTpl && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpenNest(null)}
        >
          <div
            className="gb-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-extrabold text-[#111] tracking-tighter">
                {nestMeta[activeTpl.nest_id]?.fullName || activeTpl.nest_id}
              </h3>
              <button
                onClick={() => setOpenNest(null)}
                className="text-[#717171] hover:text-[#111] text-sm"
              >
                Close
              </button>
            </div>
            <div className="mt-4 space-y-3">
              {[
                ["address", "Address", "textarea"],
                ["maps_url", "Google Maps link", "text"],
                ["arrival_instructions", "Arrival instructions (step-by-step)", "textarea"],
                ["door_code", "Mailbox / lockbox code", "text"],
                ["wifi_name", "Wi-Fi name", "text"],
                ["wifi_password", "Wi-Fi password", "text"],
                ["netflix_profile", "Netflix profile name", "text"],
                ["netflix_pin", "Netflix profile PIN", "text"],
                ["checkin_time", "Check-in time (e.g. 15:00)", "text"],
                ["checkout_time", "Check-out time (e.g. 12:00)", "text"],
                ["parking", "Parking notes", "textarea"],
                ["host_contact", "Your WhatsApp/phone (with country code)", "text"],
              ].map(([field, label, kind]) => (
                <div key={field}>
                  <label className="block text-xs font-medium text-[#717171] mb-1">
                    {label}
                  </label>
                  {kind === "textarea" ? (
                    <textarea
                      data-testid={`tpl-${field}`}
                      className="gb-input py-2 text-sm resize-none min-h-[64px]"
                      value={activeTpl[field] || ""}
                      onChange={(e) =>
                        setField(activeTpl.nest_id, field, e.target.value)
                      }
                    />
                  ) : (
                    <input
                      data-testid={`tpl-${field}`}
                      className="gb-input py-2 text-sm"
                      value={activeTpl[field] || ""}
                      onChange={(e) =>
                        setField(activeTpl.nest_id, field, e.target.value)
                      }
                    />
                  )}
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-[#717171] mb-1">
                  House rules (one per line)
                </label>
                <textarea
                  data-testid="tpl-house_rules"
                  className="gb-input py-2 text-sm resize-none min-h-[96px]"
                  value={(activeTpl.house_rules || []).join("\n")}
                  onChange={(e) =>
                    setRulesText(activeTpl.nest_id, e.target.value)
                  }
                />
              </div>
            </div>
            <button
              data-testid="tpl-save"
              onClick={() => onSave(activeTpl.nest_id)}
              disabled={saving}
              className="gb-btn-primary w-full mt-5 inline-flex items-center justify-center gap-2"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TrafficPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const nestNameById = useMemo(() => {
    const m = {};
    PROPERTIES.forEach((p) => (m[p.id] = p.fullName));
    return m;
  }, []);

  const load = () => {
    setLoading(true);
    adminGetAnalytics()
      .then(setData)
      .catch(() => toast.error("Could not load analytics"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(id);
  }, []);

  return (
    <div className="mt-12">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[#111111] font-semibold">
            Traffic today
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#111] tracking-tighter mt-2">
            Who’s peeking at your check-in.
          </h2>
        </div>
        <button
          type="button"
          data-testid="traffic-refresh"
          onClick={load}
          className="gb-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3 shrink-0"
          title="Refresh"
        >
          <RefreshCcw size={12} /> Refresh
        </button>
      </div>

      {loading && !data && <p className="mt-6 text-[#717171]">Loading…</p>}

      {data && (
        <div className="mt-6 space-y-4">
          {/* Total views hero card */}
          <div className="gb-card p-6 flex items-center gap-5">
            <span className="w-14 h-14 rounded-2xl bg-[#111] text-white flex items-center justify-center">
              <Activity size={22} />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a8a8a] font-semibold">
                Views today
              </p>
              <p
                data-testid="traffic-total"
                className="font-heading text-4xl font-extrabold text-[#111] tracking-tighter leading-none mt-1"
              >
                {data.total_today}
              </p>
            </div>
          </div>

          {/* Per-nest breakdown */}
          <div className="gb-card p-5 sm:p-6">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#111] font-semibold">
              By nest
            </p>
            {data.by_nest.length === 0 ? (
              <p className="mt-3 text-sm text-[#8a8a8a]">
                No check-in views yet today.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {data.by_nest.map((n) => {
                  const max = data.by_nest[0].views || 1;
                  return (
                    <li
                      key={n.nest_id}
                      data-testid={`traffic-nest-${n.nest_id}`}
                      className="flex items-center gap-3 text-sm"
                    >
                      <span className="w-36 shrink-0 truncate text-[#111] font-medium">
                        {nestNameById[n.nest_id] || n.nest_id}
                      </span>
                      <div className="flex-1 bg-[#f2f2f2] rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-[#111] h-full rounded-full"
                          style={{ width: `${(n.views / max) * 100}%` }}
                        />
                      </div>
                      <span className="w-10 text-right text-[#111] font-semibold">
                        {n.views}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Top reservations */}
          {data.top_reservations.length > 0 && (
            <div className="gb-card p-5 sm:p-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#111] font-semibold">
                Top guest links today
              </p>
              <ul className="mt-3 space-y-2">
                {data.top_reservations.map((r) => (
                  <li
                    key={r.slug}
                    data-testid={`traffic-slug-${r.slug}`}
                    className="flex items-center justify-between text-sm py-1.5 border-b border-[#f0f0f0] last:border-0"
                  >
                    <span className="min-w-0 truncate">
                      <span className="text-[#111] font-medium">
                        {r.guest_label}
                      </span>
                      <span className="text-[#a1a1a1] ml-2">/c/{r.slug}</span>
                    </span>
                    <span className="text-[#111] font-semibold ml-3">
                      {r.views}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReservationsPanel() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    nest_id: "nest-1",
    guest_label: "",
    guest_phone: "",
    expires_at: "",
  });
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    adminListReservations()
      .then(setReservations)
      .catch(() => toast.error("Could not load reservations"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const nestNameById = useMemo(() => {
    const m = {};
    PROPERTIES.forEach((p) => (m[p.id] = p.fullName));
    return m;
  }, []);

  const buildWaHref = (nestName, slug, phoneDigits, guestLabel) => {
    const url = `${origin}/c/${slug}`;
    // Extract just the guest's name from labels like "Menon · Aug 15-20"
    const guestName =
      (guestLabel || "").split(/[·|,\-–—]/)[0].trim() || "there";
    const msg = encodeURIComponent(
      `Hi ${guestName}! It's Dimas, your host here.\nThank you so much for booking my place.\n\nYou can find all the self-check-in details and handy information for your stay right here:\n${url}\n\nHope you have a great stay!\nCheers,\nDimas`,
    );
    return phoneDigits
      ? `https://wa.me/${phoneDigits}?text=${msg}`
      : `https://wa.me/?text=${msg}`;
  };

  const onCreate = async (e, sendWhatsApp = false) => {
    if (e) e.preventDefault();
    setCreating(true);
    try {
      const r = await adminCreateReservation({
        nest_id: form.nest_id,
        guest_label: form.guest_label.trim() || null,
        guest_phone: form.guest_phone.trim() || null,
        expires_at: form.expires_at || null,
      });
      setReservations((cur) => [r, ...cur]);
      setForm({
        nest_id: form.nest_id,
        guest_label: "",
        guest_phone: "",
        expires_at: "",
      });
      const url = `${origin}/c/${r.slug}`;
      if (sendWhatsApp) {
        const digits = (r.guest_phone || "").replace(/\D/g, "");
        const nestName = nestNameById[r.nest_id] || r.nest_id;
        window.open(
          buildWaHref(nestName, r.slug, digits, r.guest_label),
          "_blank",
        );
        toast.success("Link created — opening WhatsApp");
      } else {
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Link copied to clipboard");
        } catch {
          toast.success("Reservation created");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (slug) => {
    const url = `${origin}/c/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this check-in link?")) return;
    try {
      await adminDeleteReservation(id);
      setReservations((cur) => cur.filter((r) => r.id !== id));
      toast.success("Revoked");
    } catch {
      toast.error("Revoke failed");
    }
  };

  return (
    <div className="mt-16">
      <div>
        <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[#111111] font-semibold">
          Guest check-in links
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#111] tracking-tighter mt-2">
          One link per guest.
        </h2>
        <p className="text-sm text-[#5b5b5b] mt-2 max-w-xl">
          Pick a nest, add a label so you know who this is for, and (optionally)
          the guest&rsquo;s WhatsApp number for one-tap sending. The link opens
          instantly for anyone with it — no login required.
        </p>
      </div>

      <form
        onSubmit={onCreate}
        className="gb-card p-5 sm:p-6 mt-6 grid grid-cols-1 sm:grid-cols-6 gap-3 items-end"
      >
        <div className="sm:col-span-1">
          <label className="block text-xs font-medium text-[#717171] mb-1">
            Nest
          </label>
          <select
            data-testid="res-nest"
            className="gb-input py-2 text-sm"
            value={form.nest_id}
            onChange={(e) => setForm((f) => ({ ...f, nest_id: e.target.value }))}
          >
            {PROPERTIES.map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-[#717171] mb-1">
            Guest label (optional)
          </label>
          <input
            data-testid="res-guest-label"
            className="gb-input py-2 text-sm"
            placeholder="e.g. Menon · Aug 15-20"
            value={form.guest_label}
            onChange={(e) =>
              setForm((f) => ({ ...f, guest_label: e.target.value }))
            }
          />
        </div>
        <div className="sm:col-span-1">
          <label className="block text-xs font-medium text-[#717171] mb-1">
            WhatsApp phone (optional)
          </label>
          <input
            data-testid="res-guest-phone"
            className="gb-input py-2 text-sm"
            placeholder="+62 812…"
            inputMode="tel"
            value={form.guest_phone}
            onChange={(e) =>
              setForm((f) => ({ ...f, guest_phone: e.target.value }))
            }
          />
        </div>
        <div className="sm:col-span-1">
          <label className="block text-xs font-medium text-[#717171] mb-1">
            Auto-revoke on (optional)
          </label>
          <input
            data-testid="res-expires-at"
            className="gb-input py-2 text-sm"
            type="date"
            min={new Date().toISOString().split("T")[0]}
            value={form.expires_at}
            onChange={(e) =>
              setForm((f) => ({ ...f, expires_at: e.target.value }))
            }
          />
        </div>
        <div className="sm:col-span-1 flex flex-col gap-2">
          <button
            data-testid="res-create-btn"
            type="submit"
            disabled={creating}
            className="gb-btn-primary inline-flex items-center justify-center gap-2 text-sm py-2"
          >
            <Plus size={14} /> {creating ? "…" : "Create"}
          </button>
          <button
            data-testid="res-create-send-btn"
            type="button"
            onClick={(e) => onCreate(e, true)}
            disabled={creating || !form.guest_phone.trim()}
            title={
              form.guest_phone.trim()
                ? "Create the link and open WhatsApp"
                : "Enter a WhatsApp number to enable"
            }
            className="text-xs inline-flex items-center justify-center gap-1 py-2 px-3 rounded-full text-white bg-[#25D366] hover:bg-[#1DA851] disabled:opacity-50"
          >
            <MessageCircle size={12} /> Create &amp; send
          </button>
        </div>
      </form>

      {loading && <p className="mt-8 text-[#717171]">Loading…</p>}

      {!loading && reservations.length === 0 && (
        <div className="mt-8 gb-card p-8 text-center text-[#717171]">
          No reservations yet. Create one above.
        </div>
      )}

      {!loading && reservations.length > 0 && (
        <div className="mt-6 space-y-3">
          {reservations.map((r) => {
            const url = `${origin}/c/${r.slug}`;
            const nestName = nestNameById[r.nest_id] || r.nest_id;
            const digits = (r.guest_phone || "").replace(/\D/g, "");
            const waHref = buildWaHref(nestName, r.slug, digits, r.guest_label);
            const expired =
              r.expires_at &&
              new Date(r.expires_at) < new Date(new Date().toDateString());
            return (
              <div
                key={r.id}
                data-testid={`res-row-${r.id}`}
                className={`gb-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${
                  expired ? "opacity-60" : ""
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-[#111] text-sm">
                    {r.guest_label || "Unlabeled guest"}{" "}
                    <span className="text-[#717171]">· {nestName}</span>
                    {expired && (
                      <span className="ml-2 inline-block bg-[#faf4ea] text-[#8a5a15] text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full">
                        Expired
                      </span>
                    )}
                  </p>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#111] underline underline-offset-2 truncate block mt-1"
                  >
                    {url.replace(/^https?:\/\//, "")}
                  </a>
                  <p className="text-[10px] text-[#a1a1a1] mt-1">
                    Created{" "}
                    {new Date(r.created_at).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                    {r.guest_phone && <span> · WhatsApp {r.guest_phone}</span>}
                    {r.expires_at && (
                      <span>
                        {" "}· Revokes on{" "}
                        {new Date(r.expires_at).toLocaleDateString(undefined, {
                          dateStyle: "medium",
                        })}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 self-start sm:self-center flex-wrap">
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`res-whatsapp-${r.id}`}
                    className="text-xs inline-flex items-center gap-1 py-1.5 px-3 rounded-full text-white bg-[#25D366] hover:bg-[#1DA851]"
                    title={
                      r.guest_phone
                        ? `Send to ${r.guest_phone}`
                        : "Compose in WhatsApp (choose contact)"
                    }
                  >
                    <MessageCircle size={12} />{" "}
                    {r.guest_phone ? "Send" : "WhatsApp"}
                  </a>
                  <button
                    data-testid={`res-copy-${r.id}`}
                    onClick={() => copyLink(r.slug)}
                    className="gb-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3"
                  >
                    <Copy size={12} /> Copy
                  </button>
                  <button
                    data-testid={`res-delete-${r.id}`}
                    onClick={() => revoke(r.id)}
                    className="text-xs inline-flex items-center gap-1 py-1.5 px-3 rounded-full text-white bg-[#111111] hover:bg-[#000000]"
                  >
                    <Trash2 size={12} /> Revoke
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const pw = getAdminPassword();
    if (!pw) {
      setChecking(false);
      return;
    }
    adminVerify(pw)
      .then(() => setAuthed(true))
      .catch(() => clearAdminPassword())
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <section className="max-w-md mx-auto px-6 pt-24 text-center text-[#717171]">
        Checking session...
      </section>
    );
  }

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-6 pt-10 sm:pt-16 pb-24">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[#111111] font-semibold">
            Host dashboard
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#111] tracking-tighter mt-2">
            Welcome back.
          </h1>
        </div>
        <button
          data-testid="admin-signout"
          onClick={() => {
            clearAdminPassword();
            setAuthed(false);
            toast.message("Signed out");
          }}
          className="gb-btn-ghost inline-flex items-center gap-2 text-sm"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <TrafficPanel />
      <ReservationsPanel />
      <CheckinTemplatesPanel />

      <div className="mt-12">
        <QRCodesPanel />
      </div>

      <EntriesPanel />
    </section>
  );
}
